<template>
  <div class="venue-item md:flex">
    <div class="relative md:w-1/2">
      <VSlickCarousel v-if="venue.photos && venue.photos.length" :arrows="false" :dots="true" class="overflow-hidden">
        <a v-for="(photo, idx) in venue.photos" :key="idx" :href="`/${venue.path}`" class="relative block overflow-hidden pb-35% md:pb-56%">
          <div
            class="absolute flex size-full justify-center bg-gray-400 bg-cover align-middle"
            :style="{ 'background-image': `url(${photo.photo_rectangular})` }"
          />
        </a>
      </VSlickCarousel>
      <div class="absolute right-0 top-0 mt-2 block p-2">
        <VenueFavorite :venue="venue" color="#FFFFFF" />
      </div>
      <div v-if="showScore" class="absolute bottom-0 right-0 mb-4 mr-5 block size-8 rounded-full bg-lime5 text-center font-bold leading-8">
        {{ Math.floor(venue.tripexpert_score / 10) }}
      </div>
      <a href="#" class="absolute left-0 top-0 mt-2 block p-3" @click.prevent="$emit('close')">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 9.46788L1.46794 16L0 14.532L6.53206 7.99992L0.000156405 1.46796L1.4681 0L8 6.53196L14.5319 0L15.9998 1.46796L9.46794 7.99992L16 14.532L14.5321 16L8 9.46788Z" fill="#ffffff" />
        </svg>
      </a>
    </div>
    <div ref="details" class="p-2 pt-1 md:w-1/2 md:p-4">
      <div class="mb-1 truncate">
        <NuxtLink class="venue-name relative h-4 text-xl font-bold leading-none text-slate" :to="`/${venue.path}`">
          {{ venue.name }}
        </NuxtLink>
      </div>
      <div class="venue-description mb-2 overflow-hidden text-body" :class="{ 'description-longer': venue.venue_type_id !== 1 }" v-html="featuredHtml" />
      <div v-if="venue.venue_type_id === 1">
        <div v-if="venue.availability || !filters.check_in" class="flex justify-between">
          <div v-if="venue.low_rate || venue.price" class="p-3 pl-4 text-center">
            <span class="text-bg font-medium text-gray-1">
              {{ $f.currency(venue.price || venue.low_rate, currency) }}
            </span>
            <a v-if="venue.booking_url" :href="bookingUrl(venue, filters)" target="_blank" rel="noopener noreferrer" class="text-smm text-body hover:underline">on Booking.com</a>
          </div>
          <a
            v-if="venue.booking_url"
            :href="bookingUrl(venue, filters)"
            target="_blank"
            rel="noopener noreferrer"
            class="letter-sp15 mt-2 h-10 rounded-md bg-blue px-8 text-sm uppercase leading-10 text-white hover:bg-blue-dark"
            @click="$gtag.event('book-now')"
          >Book now</a>
        </div>
        <div v-else class="text-right">
          <div class="text-lg font-medium text-body">
            No rooms available
          </div>
          <a href="#" class="text-smm text-body underline" @click.prevent="$eventHub.emit('show-travel-dates-filter')">Try other dates</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { mapState } from 'pinia'
import { VSlickCarousel } from 'v-slick-carousel'
import bookingUrl from '~/mixins/booking-url'
import VenueFavorite from '~/components/shared/VenueFavorite'
import { useVenuesStore } from '~/stores/venues'
import { useCurrenciesStore } from '~/stores/currencies'

dayjs.extend(customParseFormat)

export default {
  components: { VenueFavorite, VSlickCarousel },

  mixins: [bookingUrl],

  props: {
    venue: { type: Object, required: true },
    showScore: Boolean,
  },

  emits: ['close'],

  computed: {
    ...mapState(useVenuesStore, ['filters']),
    ...mapState(useCurrenciesStore, ['currency']),

    featuredHtml () {
      if (! this.venue.featured_extract) { return '' }
      const clean = this.venue.featured_extract.replace(/(<([^>]+)>)/ig, '')
      const idx = clean.lastIndexOf('-')

      return clean.substr(0, idx) + (idx === -1 ? '' : `<span class="italic">${clean.substr(idx).replace('-', '—')}</span>`)
    },
  },
}
</script>

<style lang="scss" scoped>
.venue-description {
  height: 40px;
  line-height: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.description-longer {
  height: 120px;
  -webkit-line-clamp: 6;
}

:deep() {
  .v-slick-dots {
    position: absolute;
    bottom: 14px;
    li {
      margin: 0;
      button {
        &:before { color: white; opacity: 1; font-size: 9px; }
      }
    }
  }
  .v-slick-dots li.slick-active button:before {
    @apply text-gray-1 opacity-100;
  }
}
</style>
